/**
 * Save client for WorkingCopyPort.writeSection — the in-flight latch that
 * `working-copy-port.ts`'s own doc comment names as "a save-client concern
 * (TG-8), not this port's."
 *
 * ADR-20260821-5wwj (carried forward): exactly one write per section, on
 * explicit confirmation only. No debounce, no autosave, no timer, no
 * background write. This module issues a write ONLY when {@link saveSection}
 * is called by a caller reacting to an explicit customer action — it starts
 * no timer, schedules no retry and never calls itself.
 *
 * The latch: two activations of the confirm control in quick succession for
 * the SAME (renewalId, sectionId) must produce exactly one network request
 * (AC-4), not two. This is done with a plain synchronous `Map` keyed on
 * that pair — no debounce utility, no `setTimeout`-based coalescing window.
 * A second call arriving while the first is still in flight is handed the
 * SAME promise the first call is already awaiting, rather than issuing a
 * second call to the port. The entry is removed as soon as that promise
 * settles (success or failure alike), so:
 *
 *   - nothing is remembered once the write has settled — there is no
 *     client-held idempotency key surviving past the request itself
 *     (ADR-20260826-mx7t), and certainly nothing surviving a reload (this
 *     is in-memory module state only; a reload re-creates the module and
 *     starts with an empty map);
 *   - a LATER confirmation of the same section (a genuinely new customer
 *     action, after the previous write has already settled) is not
 *     suppressed — it is a new write, exactly as ADR-20260826-mx7t's
 *     upsert-by-resource-identity model expects.
 *
 * This module does not interpret the write's outcome — it forwards
 * whatever {@link WriteOutcome} the port produces unchanged. Mapping that
 * outcome to customer-facing behaviour (retained answers, distinct Failed/
 * Indeterminate wording) is `features/renewal-capture/confirm.ts`'s job.
 *
 * FIND-35/OBJ-20: `WorkingCopyPort.writeSection` documents no never-
 * rejects guarantee, and a real HTTP-backed implementation rejects on
 * exactly the canonical Indeterminate trigger — a network drop mid-flight
 * (ADR-20260821-kp45's own "unknown, check before resubmitting"). A
 * rejected promise reaching `confirmSection`'s `switch (outcome.status)`
 * would never enter any case, leaving `handleConfirm` awaiting forever
 * with no outcome rendered and the confirm control's saving latch never
 * released. This module is the owning seam for that translation — every
 * caller of {@link saveSection} already treats its return value as the
 * closed {@link WriteOutcome} union, so a caught rejection here is mapped
 * to `Indeterminate` (a dispatched write whose fate is unknown IS this
 * outcome's definition) rather than needing every downstream consumer to
 * separately guard against a promise that never settles into an
 * outcome.
 */
import type { WriteOutcome } from "./outcome";
import type { WorkingCopyPort, WorkingCopyWriteResult } from "./working-copy-port";

/** Turns an unexpected `writeSection` rejection into the closed
 * `WriteOutcome` union's `Indeterminate` member. `httpStatus: 0` marks
 * "no HTTP response was ever received" — distinct from any real status
 * code the taxonomy names — since a rejection means the request never
 * completed at all. */
function toIndeterminateOutcome(error: unknown): WriteOutcome<WorkingCopyWriteResult> {
  return {
    status: "Indeterminate",
    detail: {
      httpStatus: 0,
      message: error instanceof Error ? error.message : String(error),
    },
  };
}

function latchKey(renewalId: string, sectionId: string): string {
  // A NUL separator can never appear in either id in practice, but the
  // point of using one at all (rather than a template literal with a
  // human-readable separator like "/") is to make an accidental collision
  // between (renewalId="a/b", sectionId="c") and (renewalId="a", sectionId=
  // "b/c") structurally implausible rather than merely unlikely.
  return `${renewalId}\0${sectionId}`;
}

const writesInFlight = new Map<string, Promise<WriteOutcome<WorkingCopyWriteResult>>>();

/**
 * Writes one section's answers via `port.writeSection`, exactly once per
 * distinct confirmation. If a write for the same (renewalId, sectionId) is
 * already in flight, this call returns that SAME promise instead of issuing
 * a second request — the latch AC-4 requires.
 */
export function saveSection(
  port: WorkingCopyPort,
  renewalId: string,
  sectionId: string,
  answers: Record<string, unknown>,
): Promise<WriteOutcome<WorkingCopyWriteResult>> {
  const key = latchKey(renewalId, sectionId);
  const inFlight = writesInFlight.get(key);
  if (inFlight !== undefined) {
    return inFlight;
  }

  // FIND-37/OBJ-22: calling `port.writeSection(...)` directly (as the
  // argument expression to `.catch`) evaluates it eagerly, before `.catch`
  // ever attaches — a port that throws synchronously (rather than
  // returning a rejected promise) would escape this module uncaught. The
  // `try`/`catch` below keeps the call synchronous (so the latch and the
  // deferred-port test helpers still observe the call on the same tick as
  // `saveSection` itself), while still routing a synchronous throw into the
  // same `Promise.resolve(...).catch(toIndeterminateOutcome)` chain an
  // asynchronous rejection already goes through.
  let pending: Promise<WriteOutcome<WorkingCopyWriteResult>>;
  // This `try` is deliberately synchronous (see FIND-37/OBJ-22 above): it
  // exists only to turn a synchronous throw from `port.writeSection` into
  // the same rejected-promise shape an asynchronous rejection already
  // takes, not to await the write itself. Awaiting here would defer the
  // call past the current tick, which is exactly what the latch and the
  // deferred-port test helpers rely on not happening. The rejection path
  // typescript:S4822 wants covered by `await`/`.catch` is covered -- by the
  // `.catch(toIndeterminateOutcome)` two lines below, chained onto
  // `pending` regardless of which branch set it.
  try { // NOSONAR typescript:S4822 -- see comment above; rejection is handled by the .catch() chained below, deliberately not awaited here
    pending = Promise.resolve(port.writeSection(renewalId, sectionId, answers));
  } catch (error) {
    pending = Promise.resolve(toIndeterminateOutcome(error));
  }
  const write = pending.catch(toIndeterminateOutcome).finally(() => {
    writesInFlight.delete(key);
  });
  writesInFlight.set(key, write);
  return write;
}
